import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Building2, Upload, Users, Palette, CreditCard, Info, UserPlus,
  Crown, Shield, Eye, User, Receipt, Trash2, Handshake, Globe,
  CheckCircle2, Send, Server, Settings2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';
import { Button } from './Button';
import { Input } from './Input';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';
import { Skeleton } from './Skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { toast } from 'sonner';
import styles from './OrgSettingsDialog.module.css';

// ─── Types ───
interface TenantDetails {
  tenant: {
    id: string; name: string; slug: string; type: string;
    logoUrl: string | null; emailDomain: string | null;
    authMode: string; mfaRequired: number; isActive: number; createdAt: string;
  };
  subscription: {
    tier: string; status: string; seatLimit: number; seatCount: number;
    workerLimit: number; projectLimit: number; storageLimitMb: number;
    currentPeriodEnd: string | null;
  };
  partner: { id: string; name: string; slug: string; logoUrl: string | null } | null;
  callerRole: string;
}

interface Member {
  userId: number; role: string; joinedAt: string; isActive: number;
  email: string; displayName: string; avatarUrl: string | null;
}

// ─── API helpers ───
async function fetchTenantDetails(): Promise<TenantDetails | null> {
  try {
    const res = await fetch('/_api/tenants/details', { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

async function fetchMembers(): Promise<Member[]> {
  try {
    const res = await fetch('/_api/tenants/members', { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json() as any;
    return data.members || [];
  } catch { return []; }
}

async function updateTenant(updates: Record<string, any>): Promise<boolean> {
  try {
    const res = await fetch('/_api/tenants/update', {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return res.ok;
  } catch { return false; }
}

async function uploadLogo(dataUrl: string, logoType: 'logo' | 'favicon'): Promise<string | null> {
  try {
    const res = await fetch('/_api/tenants/upload-logo', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetType: 'tenant', dataUrl, logoType }),
    });
    if (!res.ok) return null;
    const data = await res.json() as any;
    return data.url;
  } catch { return null; }
}

async function inviteMember(email: string, role: string): Promise<any> {
  const res = await fetch('/_api/tenants/members', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as any;
    throw new Error(err.error || 'Failed to invite member');
  }
  return res.json();
}

// ─── Component ───
export const OrgSettingsDialog = () => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [details, setDetails] = useState<TenantDetails | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  // Branding state
  const [tenantName, setTenantName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);

  // Partner application state (form moved to /tsp-apply page)

  // DNS / Domain state (partner)
  const [customDomain, setCustomDomain] = useState('');
  const [savingDns, setSavingDns] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<{ type: string; name: string; value: string; status: string }[]>([]);

  // Managed tenants (partner)
  const [managedTenants, setManagedTenants] = useState<{ id: string; name: string; slug: string; tier: string; seatCount: number; isActive: number }[]>([]);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [d, m] = await Promise.all([fetchTenantDetails(), fetchMembers()]);
    setDetails(d);
    setMembers(m);
    if (d) {
      setTenantName(d.tenant.name || '');
      setLogoUrl(d.tenant.logoUrl);
      setBrandName((d.tenant as any).brandName || '');
      setPrimaryColor((d.tenant as any).primaryColor || '#6366f1');
    }
    // Load partner-specific data if applicable
    if (d?.partner) {
      try {
        const partnersRes = await fetch(`/_api/tenants/partners?id=${d.partner.id}`, { credentials: 'include' });
        if (partnersRes.ok) {
          const partnerData = await partnersRes.json() as any;
          if (partnerData.customDomain) {
            setCustomDomain(partnerData.customDomain);
            setDnsRecords([{
              type: 'CNAME', name: partnerData.customDomain,
              value: 'tasking.tech', status: partnerData.sslStatus || 'pending',
            }]);
          }
          setManagedTenants(partnerData.tenants || []);
        }
      } catch { /* partner data not yet available */ }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  const isAdmin = details?.callerRole === 'owner' || details?.callerRole === 'admin';
  const isPersonal = details?.tenant.type === 'personal';

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon') => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      const url = await uploadLogo(dataUrl, type);
      if (url) {
        if (type === 'logo') setLogoUrl(url);
        else setFaviconUrl(url);
        toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded`);
      } else {
        toast.error('Upload failed');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    const updates: Record<string, any> = { name: tenantName };
    if (brandName.trim()) updates.brandName = brandName.trim();
    if (primaryColor !== '#6366f1') updates.primaryColor = primaryColor;
    const ok = await updateTenant(updates);
    if (ok) toast.success('Branding saved — refresh to see changes');
    else toast.error('Failed to save');
    setSaving(false);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      toast.success(`Invited ${inviteEmail}`);
      setInviteEmail('');
      const m = await fetchMembers();
      setMembers(m);
    } catch (err: any) {
      toast.error(err.message);
    }
    setInviting(false);
  };

  const handleSaveDns = async () => {
    if (!customDomain.trim()) {
      toast.error('Enter a custom domain');
      return;
    }
    setSavingDns(true);
    try {
      const res = await fetch('/_api/tenants/setup-domain', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetType: details?.partner ? 'partner' : 'tenant',
          domain: customDomain.trim(),
        }),
      });
      if (res.ok) {
        const data = await res.json() as any;
        setDnsRecords([{
          type: 'CNAME',
          name: customDomain.trim(),
          value: data.cnameTarget || 'tasking.tech',
          status: data.sslStatus || 'pending',
        }]);
        toast.success('Custom domain saved. Add the CNAME record to your DNS provider.');
      } else {
        const err = await res.json().catch(() => ({})) as any;
        toast.error(err.error || 'Failed to save domain');
      }
    } catch {
      toast.error('Failed to save domain');
    }
    setSavingDns(false);
  };

  const roleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown size={12} />;
      case 'admin': return <Shield size={12} />;
      case 'viewer': return <Eye size={12} />;
      default: return <User size={12} />;
    }
  };

  const roleClass = (role: string) => {
    switch (role) {
      case 'owner': return styles.roleOwner;
      case 'admin': return styles.roleAdmin;
      case 'member': return styles.roleMember;
      default: return styles.roleViewer;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon-md"
              aria-label="Workspace Settings"
              className={styles.triggerButton}
            >
              <Building2 className={styles.icon} />
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Workspace</TooltipContent>
      </Tooltip>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader className={styles.dialogHeader}>
          <DialogTitle className={styles.dialogTitle}>
            <Building2 size={20} className={styles.titleIcon} />
            {details?.tenant.name || 'Workspace Settings'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className={styles.tabs}>
          <TabsList className={styles.tabsList}>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
            <TabsTrigger value="partner">
              <Handshake size={14} style={{ marginRight: 4 }} /> Partner
            </TabsTrigger>
          </TabsList>

          <div className={styles.tabContentWrapper}>
            {/* ── Branding Tab ── */}
            <TabsContent value="branding" className={styles.tabContent}>
              <div className={styles.scrollableContent}>
                {loading ? (
                  <div className={styles.loading}>
                    <Skeleton style={{ height: 80 }} />
                    <Skeleton style={{ height: 40 }} />
                    <Skeleton style={{ height: 40 }} />
                  </div>
                ) : (
                  <>
                    {isPersonal && (
                      <div className={styles.infoBanner}>
                        <Info size={18} />
                        <span>This is your personal workspace. Upgrade to a Team or Enterprise plan to customize branding for your organization.</span>
                      </div>
                    )}

                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Upload size={16} /> Logo & Favicon
                      </h3>
                      <div className={styles.logoSection}>
                        <div>
                          <div className={styles.logoPreview}>
                            {logoUrl ? (
                              <img src={logoUrl} alt="Logo" />
                            ) : (
                              <Building2 size={32} className={styles.logoPlaceholder} />
                            )}
                          </div>
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo')}
                          />
                          {isAdmin && (
                            <Button size="sm" variant="secondary" className={styles.uploadBtn} onClick={() => logoInputRef.current?.click()}>
                              Upload Logo
                            </Button>
                          )}
                        </div>
                        <div>
                          <div className={styles.faviconPreview}>
                            {faviconUrl ? (
                              <img src={faviconUrl} alt="Favicon" />
                            ) : (
                              <Building2 size={16} className={styles.logoPlaceholder} />
                            )}
                          </div>
                          <input
                            ref={faviconInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'favicon')}
                          />
                          {isAdmin && (
                            <Button size="sm" variant="secondary" className={styles.uploadBtn} onClick={() => faviconInputRef.current?.click()}>
                              Upload Favicon
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Palette size={16} /> Brand Settings
                      </h3>
                      <p className={styles.sectionDesc}>
                        Customize how your workspace appears. The logo and brand name replace the default tasking.tech branding in the header.
                      </p>
                      <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Workspace Name</label>
                        <Input
                          value={tenantName}
                          onChange={(e) => setTenantName(e.target.value)}
                          placeholder="My Organization"
                          disabled={!isAdmin}
                        />
                      </div>
                      <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Brand Display Name</label>
                        <Input
                          value={brandName}
                          onChange={(e) => setBrandName(e.target.value)}
                          placeholder="Your Company"
                          disabled={!isAdmin}
                        />
                        <span className={styles.fieldHint}>
                          Replaces "tasking.tech" in the header and throughout the UI
                        </span>
                      </div>
                      <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Primary Color</label>
                        <div className={styles.colorPickerRow}>
                          <div className={styles.colorSwatch}>
                            <input
                              type="color"
                              value={primaryColor}
                              onChange={(e) => setPrimaryColor(e.target.value)}
                              disabled={!isAdmin}
                            />
                          </div>
                          <span className={styles.colorHex}>{primaryColor}</span>
                        </div>
                      </div>

                      {isAdmin && (
                        <Button onClick={handleSaveBranding} disabled={saving}>
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* ── Members Tab ── */}
            <TabsContent value="members" className={styles.tabContent}>
              <div className={styles.scrollableContent}>
                {loading ? (
                  <div className={styles.loading}>
                    <Skeleton style={{ height: 56 }} />
                    <Skeleton style={{ height: 56 }} />
                    <Skeleton style={{ height: 56 }} />
                  </div>
                ) : (
                  <>
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Users size={16} /> Members ({members.length})
                      </h3>
                      {details?.subscription && (
                        <p className={styles.sectionDesc}>
                          {details.subscription.seatCount} of {details.subscription.seatLimit} seats used
                        </p>
                      )}

                      <div className={styles.membersList}>
                        {members.map((m) => (
                          <div key={m.userId} className={styles.memberRow}>
                            <div className={styles.memberAvatar}>
                              {m.avatarUrl ? (
                                <img src={m.avatarUrl} alt="" />
                              ) : (
                                (m.displayName || m.email).charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className={styles.memberInfo}>
                              <div className={styles.memberName}>{m.displayName || m.email}</div>
                              <div className={styles.memberEmail}>{m.email}</div>
                            </div>
                            <span className={`${styles.memberRole} ${roleClass(m.role)}`}>
                              {roleIcon(m.role)} {m.role}
                            </span>
                          </div>
                        ))}
                        {members.length === 0 && (
                          <div className={styles.empty}>No members found</div>
                        )}
                      </div>
                    </div>

                    {isAdmin && !isPersonal && (
                      <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>
                          <UserPlus size={16} /> Invite Member
                        </h3>
                        <div className={styles.inviteRow}>
                          <Input
                            type="email"
                            placeholder="email@example.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                          />
                          <Select value={inviteRole} onValueChange={setInviteRole}>
                            <SelectTrigger style={{ width: 120 }}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
                            {inviting ? 'Inviting...' : 'Invite'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>

            {/* ── Subscription Tab ── */}
            <TabsContent value="subscription" className={styles.tabContent}>
              <div className={styles.scrollableContent}>
                {loading ? (
                  <div className={styles.loading}>
                    <Skeleton style={{ height: 120 }} />
                  </div>
                ) : details?.subscription ? (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      <CreditCard size={16} /> Current Plan
                    </h3>
                    <div className={styles.subCard}>
                      <div className={styles.subTier}>
                        {details.subscription.tier}
                        <span className={`${styles.statusBadge} ${details.subscription.status === 'active' ? styles.statusActive : styles.statusInactive}`} style={{ marginLeft: 8 }}>
                          {details.subscription.status}
                        </span>
                      </div>
                      <div className={styles.subGrid}>
                        <div className={styles.subStat}>
                          <div className={styles.subStatLabel}>Seats</div>
                          <div className={styles.subStatValue}>
                            {details.subscription.seatCount} / {details.subscription.seatLimit}
                          </div>
                        </div>
                        <div className={styles.subStat}>
                          <div className={styles.subStatLabel}>Projects</div>
                          <div className={styles.subStatValue}>
                            {details.subscription.projectLimit}
                          </div>
                        </div>
                        <div className={styles.subStat}>
                          <div className={styles.subStatLabel}>Workers</div>
                          <div className={styles.subStatValue}>
                            {details.subscription.workerLimit}
                          </div>
                        </div>
                        <div className={styles.subStat}>
                          <div className={styles.subStatLabel}>Storage</div>
                          <div className={styles.subStatValue}>
                            {details.subscription.storageLimitMb >= 1024
                              ? `${(details.subscription.storageLimitMb / 1024).toFixed(0)} GB`
                              : `${details.subscription.storageLimitMb} MB`}
                          </div>
                        </div>
                      </div>
                      {details.subscription.currentPeriodEnd && (
                        <p className={styles.fieldHint} style={{ marginTop: 12 }}>
                          Current period ends {new Date(details.subscription.currentPeriodEnd).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {details.partner && (
                      <div className={styles.infoBanner} style={{ marginTop: 16 }}>
                        <Info size={18} />
                        <span>
                          This workspace is managed by <strong>{details.partner.name}</strong>.
                          Contact your partner for subscription changes.
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.empty}>No subscription information available</div>
                )}
              </div>
            </TabsContent>

            {/* ── Partner Tab ── */}
            <TabsContent value="partner" className={styles.tabContent}>
              <div className={styles.scrollableContent}>
                {details?.partner ? (
                  <>
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Handshake size={16} /> Partner Status
                      </h3>
                      <div className={styles.subCard}>
                        <div className={styles.subTier}>
                          {details.partner.name}
                          <span className={`${styles.statusBadge} ${styles.statusActive}`} style={{ marginLeft: 8 }}>
                            Active Partner
                          </span>
                        </div>
                        <p className={styles.sectionDesc} style={{ marginTop: 8 }}>
                          Slug: <code>{details.partner.slug}</code>
                        </p>
                      </div>
                    </div>

                    {/* DNS / Custom Domain */}
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Globe size={16} /> Custom Domain (White-Label)
                      </h3>
                      <p className={styles.sectionDesc}>
                        Set a custom domain so your tenants access the platform via your own branded URL.
                      </p>
                      <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Custom Domain</label>
                        <div className={styles.inviteRow}>
                          <Input
                            value={customDomain}
                            onChange={(e) => setCustomDomain(e.target.value)}
                            placeholder="app.yourcompany.com"
                          />
                          <Button onClick={handleSaveDns} disabled={savingDns || !customDomain.trim()}>
                            {savingDns ? 'Saving...' : 'Save'}
                          </Button>
                        </div>
                        <span className={styles.fieldHint}>
                          After saving, add the DNS records below to your domain registrar.
                        </span>
                      </div>
                      {dnsRecords.length > 0 && (
                        <div className={styles.dnsTable}>
                          <div className={styles.dnsHeader}>
                            <span>Type</span><span>Name</span><span>Value</span><span>Status</span>
                          </div>
                          {dnsRecords.map((r, i) => (
                            <div key={i} className={styles.dnsRow}>
                              <span className={styles.dnsType}>{r.type}</span>
                              <span className={styles.dnsName}>{r.name}</span>
                              <span className={styles.dnsValue}>{r.value}</span>
                              <span className={`${styles.statusBadge} ${r.status === 'verified' ? styles.statusActive : styles.statusInactive}`}>
                                {r.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Managed Tenants */}
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Server size={16} /> Managed Tenants ({managedTenants.length})
                      </h3>
                      <p className={styles.sectionDesc}>
                        Workspaces under your partner program. Each tenant has its own branding, members, and subscription.
                      </p>
                      {managedTenants.length > 0 ? (
                        <div className={styles.membersList}>
                          {managedTenants.map((t) => (
                            <div key={t.id} className={styles.memberRow}>
                              <div className={styles.memberAvatar}>
                                <Building2 size={14} />
                              </div>
                              <div className={styles.memberInfo}>
                                <div className={styles.memberName}>{t.name}</div>
                                <div className={styles.memberEmail}>{t.slug} &middot; {t.tier}</div>
                              </div>
                              <span className={`${styles.memberRole} ${t.isActive ? styles.roleAdmin : styles.roleViewer}`}>
                                {t.isActive ? 'Active' : 'Inactive'} &middot; {t.seatCount} seats
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.empty}>No tenants yet. Invite organizations to join your partner program.</div>
                      )}
                    </div>

                    {/* Subscription Settings for Partner */}
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <Settings2 size={16} /> Partner Subscription Settings
                      </h3>
                      <p className={styles.sectionDesc}>
                        Configure default subscription tiers and limits for tenants you manage.
                        Contact support for bulk pricing and enterprise arrangements.
                      </p>
                      <div className={styles.subGrid}>
                        <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                          <div className={styles.subStatLabel}>Default Tier</div>
                          <div className={styles.subStatValue}>Pro</div>
                        </div>
                        <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                          <div className={styles.subStatLabel}>Default Seats</div>
                          <div className={styles.subStatValue}>10</div>
                        </div>
                        <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                          <div className={styles.subStatLabel}>Tenants</div>
                          <div className={styles.subStatValue}>{managedTenants.length}</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      <Handshake size={16} /> Become a Tasking Service Provider
                    </h3>
                    <p className={styles.sectionDesc}>
                      Join our partner program to resell tasking.tech under your own brand. Partners get wholesale pricing,
                      white-label branding, custom domains, and the ability to manage multiple tenant workspaces.
                    </p>

                    <div className={styles.subGrid} style={{ marginBottom: '1.5rem' }}>
                      <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                        <div className={styles.subStatLabel}>Silver</div>
                        <div className={styles.subStatValue}>10%</div>
                        <div className={styles.fieldHint}>wholesale discount</div>
                      </div>
                      <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                        <div className={styles.subStatLabel}>Gold</div>
                        <div className={styles.subStatValue}>20%</div>
                        <div className={styles.fieldHint}>wholesale discount</div>
                      </div>
                      <div className={styles.subCard} style={{ textAlign: 'center', padding: '1rem' }}>
                        <div className={styles.subStatLabel}>Platinum</div>
                        <div className={styles.subStatValue}>30%</div>
                        <div className={styles.fieldHint}>wholesale discount</div>
                      </div>
                    </div>

                    <Button
                      onClick={() => { setOpen(false); window.location.href = '/tsp-apply'; }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                      <Send size={14} />
                      Start Application
                    </Button>
                    <span className={styles.fieldHint} style={{ marginTop: 8, display: 'block' }}>
                      The application includes company info, logo upload, reseller agreement signing, and tier selection.
                    </span>
                  </div>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
